import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatDate } from '../utils/format';
import { enumLabel, LEAVE_TYPE_VALUES } from '../utils/i18nLabels';

function leaveStatusLabel(t, status) {
  const k = `leaveStatuses.${status}`;
  const label = t(k);
  return label === k ? t(`status.${status}`) : label;
}

const emptyLeave = {
  employee_id: '',
  employee_name: '',
  type: 'vacation',
  start_date: '',
  end_date: '',
  days: 1,
  reason: '',
};

export function Leaves() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyLeave);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [fType, setFType] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fEmp, setFEmp] = useState('');
  const [fMonth, setFMonth] = useState('');

  async function load() {
    setLoading(true);
    setLoadErr(false);
    try {
      const [l, e] = await Promise.all([api.get('/api/leaves'), api.get('/api/employees')]);
      setRows(Array.isArray(l.data) ? l.data : []);
      setEmployees(Array.isArray(e.data) ? e.data : []);
    } catch {
      setRows([]);
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function approve(id) {
    await api.post(`/api/leaves/${id}/approve`);
    await load();
  }

  async function reject(id) {
    await api.post(`/api/leaves/${id}/reject`);
    await load();
  }

  async function create() {
    await api.post('/api/leaves', {
      ...form,
      days: Number(form.days),
    });
    setModal(false);
    setForm(emptyLeave);
    await load();
  }

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (fType && r.type !== fType) return false;
      if (fStatus && r.status !== fStatus) return false;
      if (fEmp && r.employee_id !== fEmp) return false;
      if (fMonth && r.start_date) {
        const m = r.start_date.slice(0, 7);
        if (m !== fMonth) return false;
      }
      return true;
    });
  }, [rows, fType, fStatus, fEmp, fMonth]);

  const metrics = useMemo(() => {
    const pending = rows.filter((r) => r.status === 'pending').length;
    const approved = rows.filter((r) => r.status === 'approved').length;
    const rejected = rows.filter((r) => r.status === 'rejected').length;
    const totalDays = rows.reduce((acc, r) => acc + (Number(r.days) || 0), 0);
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const approvedMonth = rows.filter((r) => r.status === 'approved' && r.start_date && r.start_date.startsWith(ym)).length;
    const upcomingCount = rows.filter((r) => (r.status === 'pending' || r.status === 'approved') && r.start_date).filter((r) => {
      const d = new Date(r.start_date);
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return !Number.isNaN(d.getTime()) && d >= t;
    }).length;
    return { pending, approved, rejected, totalDays, approvedMonth, upcomingCount };
  }, [rows]);

  const weekStrip = useMemo(() => {
    const d0 = new Date();
    d0.setHours(0, 0, 0, 0);
    const day = d0.getDay();
    const diff = (day + 6) % 7;
    const monday = new Date(d0);
    monday.setDate(d0.getDate() - diff);
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(monday);
      dt.setDate(monday.getDate() + i);
      const iso = dt.toISOString().slice(0, 10);
      const count = rows.filter((r) => {
        if (!(r.status === 'approved' || r.status === 'pending')) return false;
        if (!r.start_date || !r.end_date) return false;
        const a = new Date(r.start_date);
        const b = new Date(r.end_date);
        const cur = new Date(iso);
        return cur >= a && cur <= b;
      }).length;
      return { iso, count, label: dt.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' }) };
    });
  }, [rows]);

  const upcomingLeaves = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...rows]
      .filter((r) => (r.status === 'pending' || r.status === 'approved') && r.start_date)
      .filter((r) => {
        const d = new Date(r.start_date);
        return !Number.isNaN(d.getTime()) && d >= today;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .slice(0, 8);
  }, [rows]);

  function impactFor(days) {
    const n = Number(days) || 0;
    if (n >= 5) return { key: 'leaves.impactHigh', variant: 'error' };
    if (n >= 3) return { key: 'leaves.impactMed', variant: 'pending' };
    return { key: 'leaves.impactLow', variant: 'mutedFallback' };
  }

  if (loading && rows.length === 0) {
    return <div className="empty-state empty-state--panel">{t('leaves.loading')}</div>;
  }

  return (
    <div className="page-stack leaves-page leaves-page--v20">
      <header className="leaves-suite-hero panel panel--elevated">
        <p className="leaves-suite-hero__eyebrow">{t('leaves.suiteHeroEyebrow')}</p>
        <h1 className="leaves-suite-hero__title">{t('leaves.suiteHeroTitle')}</h1>
        <p className="leaves-suite-hero__lead">{t('leaves.heroLead')}</p>
      </header>

      {loadErr && <div className="banner banner--info">{t('leaves.errorLoad')}</div>}

      <div className="people-suite-kpis leaves-v16-kpis">
        <StatCard icon="◷" label={t('leaves.summaryPending')} value={metrics.pending} tone="warning" />
        <StatCard icon="✓" label={t('leaves.kpiApprovedMonth')} value={metrics.approvedMonth} tone="success" />
        <StatCard icon="✕" label={t('leaves.summaryRejected')} value={metrics.rejected} tone="accent" />
        <StatCard icon="◆" label={t('leaves.summaryDays')} value={metrics.totalDays} />
        <StatCard icon="◎" label={t('leaves.kpiUpcoming')} value={metrics.upcomingCount} />
      </div>

      <section className="panel panel--elevated leaves-cal-panel">
        <h2 className="panel__title">{t('leaves.calTitle')}</h2>
        <p className="panel__muted text-sm">{t('leaves.calHint')}</p>
        <div className="leaves-week-strip">
          {weekStrip.map((d) => (
            <div key={d.iso} className="leaves-week-strip__day">
              <span className="leaves-week-strip__label">{d.label}</span>
              <span className="leaves-week-strip__count">{d.count}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="leaves-v16-layout">
        <div className="leaves-v16-main">
          <div className="toolbar toolbar--card leaves-v16-toolbar leaves-v16-toolbar--filters">
            <select className="input input--select" value={fType} onChange={(e) => setFType(e.target.value)} aria-label={t('leaves.filterType')}>
              <option value="">{t('leaves.filterType')}</option>
              {LEAVE_TYPE_VALUES.map((lt) => (
                <option key={lt} value={lt}>
                  {enumLabel(t, 'leaveTypes', lt)}
                </option>
              ))}
            </select>
            <select className="input input--select" value={fStatus} onChange={(e) => setFStatus(e.target.value)} aria-label={t('leaves.filterStatus')}>
              <option value="">{t('leaves.filterStatus')}</option>
              <option value="pending">{t('leaveStatuses.pending')}</option>
              <option value="approved">{t('leaveStatuses.approved')}</option>
              <option value="rejected">{t('leaveStatuses.rejected')}</option>
              <option value="cancelled">{t('leaveStatuses.cancelled')}</option>
            </select>
            <select className="input input--select" value={fEmp} onChange={(e) => setFEmp(e.target.value)} aria-label={t('leaves.filterEmployee')}>
              <option value="">{t('leaves.filterEmployee')}</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="month"
              value={fMonth}
              onChange={(e) => setFMonth(e.target.value)}
              aria-label={t('leaves.filterPeriod')}
            />
            <div className="toolbar__grow" />
            <button type="button" className="btn btn--primary" onClick={() => setModal(true)}>
              {t('leaves.new')}
            </button>
          </div>
          <div className="table-wrap table-wrap--rounded">
            <table className="data-table data-table--comfortable">
              <thead>
                <tr>
                  <th>{t('leaves.employee')}</th>
                  <th>{t('leaves.type')}</th>
                  <th>{t('leaves.dates')}</th>
                  <th>{t('leaves.days')}</th>
                  <th>{t('leaves.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-inline">
                      {t('leaves.empty')}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((lv) => (
                    <tr key={lv.id}>
                      <td>{lv.employee_name}</td>
                  <td>
                    <span className="leaves-v16-type">{enumLabel(t, 'leaveTypes', lv.type)}</span>
                  </td>
                      <td>
                        {formatDate(lv.start_date)} — {formatDate(lv.end_date)}
                      </td>
                      <td>{lv.days}</td>
                      <td>
                        <Badge variant={lv.status === 'rejected' ? 'rejected' : lv.status}>{leaveStatusLabel(t, lv.status)}</Badge>
                      </td>
                      <td>
                        {lv.status === 'pending' ? (
                          <div className="row-actions leaves-v16-actions">
                            <button type="button" className="btn btn--sm btn--primary" onClick={() => approve(lv.id)}>
                              {t('leaves.approve')}
                            </button>
                            <button type="button" className="btn btn--sm btn--danger" onClick={() => reject(lv.id)}>
                              {t('leaves.reject')}
                            </button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="panel panel--elevated leaves-v16-aside">
          <h3 className="leaves-v16-aside__title">{t('leaves.upcomingTitle')}</h3>
          {upcomingLeaves.length === 0 ? (
            <p className="panel__muted">{t('leaves.upcomingEmpty')}</p>
          ) : (
            <ul className="leaves-v16-upcoming">
              {upcomingLeaves.map((lv) => {
                const imp = impactFor(lv.days);
                return (
                  <li key={lv.id} className="leaves-v16-upcoming__row">
                    <div>
                      <div className="leaves-v16-upcoming__name">{lv.employee_name}</div>
                      <div className="leaves-v16-upcoming__dates">
                        {formatDate(lv.start_date)} → {formatDate(lv.end_date)}
                      </div>
                      <div className="text-muted text-sm">
                        {enumLabel(t, 'leaveTypes', lv.type)}
                        {lv.area ? ` · ${lv.area}` : ''}
                      </div>
                    </div>
                    <div className="leaves-v16-upcoming__badges">
                      <Badge variant={lv.status === 'pending' ? 'pending' : 'approved'}>{leaveStatusLabel(t, lv.status)}</Badge>
                      <Badge variant={imp.variant}>{t(imp.key)}</Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('leaves.new')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('leaves.pickEmployee')}</span>
                <select
                  className="input"
                  value={form.employee_id}
                  onChange={(e) => {
                    const emp = employees.find((x) => x.id === e.target.value);
                    setForm({
                      ...form,
                      employee_id: e.target.value,
                      employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
                    });
                  }}
                >
                  <option value="">—</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('leaves.type')}</span>
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {LEAVE_TYPE_VALUES.map((lt) => (
                    <option key={lt} value={lt}>
                      {enumLabel(t, 'leaveTypes', lt)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('leaves.days')}</span>
                <input
                  className="input"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={form.days}
                  onChange={(e) => setForm({ ...form, days: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('leaves.dateFrom')}</span>
                <input
                  className="input"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('leaves.dateTo')}</span>
                <input
                  className="input"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('leaves.reason')}</span>
                <input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(false)}>
                {t('employees.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={create} disabled={!form.employee_id}>
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
